import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  Grid
} from '@mui/material';
import { 
  Refresh, 
  Visibility, 
  Download, 
  PlayArrow, 
  CheckCircle, 
  Schedule, 
  HourglassEmpty, 
  Warning, 
  ContentCopy,
  Receipt,
  AccountBalanceWallet,
  BusinessCenter
} from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface JobItem {
  id: string;
  job_id: string;
  file_name: string;
  medio_pago: string;
  job_type: string;
  username?: string;
  created_at: string;
  status: 'completed' | 'approved' | 'processing' | 'failed' | 'queued' | 'draft';
}

interface JobProgress {
  status: string;
  error?: string;
  details?: {
    operation?: string;
    current_item?: number;
    total_items?: number;
    elapsed_time?: string;
    position_in_queue?: number;
  };
}

interface JobsDashboardResultProps {
  initialJobs?: JobItem[];
  isSuperUser?: boolean;
}

const JobsDashboardResult: React.FC<JobsDashboardResultProps> = ({ 
  initialJobs = [], 
  isSuperUser = false 
}) => {
  // Debug logging
  console.log('JobsDashboardResult mounted with', Array.isArray(initialJobs) ? initialJobs.length : 0, 'jobs');

  const [jobs, setJobs] = useState<JobItem[]>(initialJobs);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobItem | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const getStatusColor = (status: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'approved': return 'info';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      case 'queued': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'approved': return <CheckCircle />;
      case 'processing': return <HourglassEmpty />;
      case 'failed': return <ErrorIcon />;
      case 'queued': return <Schedule />;
      case 'draft': return <Warning />;
      default: return <HourglassEmpty />;
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'approved': return 'Aprobado';
      case 'processing': return 'Procesando';
      case 'failed': return 'Error';
      case 'queued': return 'En Cola';
      case 'draft': return 'Borrador';
      default: return status;
    }
  };

  const getJobTypeIcon = (jobType: string): React.ReactElement => {
    switch (jobType) {
      case 'programacion_pagos': return <Schedule />;
      case 'generate_cp': return <Receipt />;
      default: return <BusinessCenter />;
    }
  };

  const getJobTypeText = (jobType: string): string => {
    switch (jobType) {
      case 'programacion_pagos': return 'Programación de Pagos';
      case 'generate_cp': return 'Generación CP';
      default: return 'Autorización de CP y Generación de CE';
    }
  };

  const getMedioPageText = (medioPage: string): string => {
    return medioPage === 'Generación CP' ? 'Generación CP' : medioPage;
  };

  const checkJobStatus = useCallback(async (jobId: string): Promise<JobProgress> => {
    const response = await fetch(`/check-job-status/${jobId}?_t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to check job status: ${response.statusText}`);
    }
    return response.json();
  }, []);

  const refreshJobs = useCallback(async () => {
    setRefreshing(true);
    try {
      const updatedJobs = await Promise.all(
        jobs.map(async (job) => {
          try {
            const progress = await checkJobStatus(job.job_id);
            return { ...job, status: progress.status as JobItem['status'] };
          } catch (error) {
            console.error(`Error checking status for job ${job.job_id}:`, error);
            return job;
          }
        })
      );
      setJobs(updatedJobs);
    } catch (error) {
      console.error('Error refreshing jobs:', error);
      setError('Error al actualizar el estado de los trabajos');
    } finally {
      setRefreshing(false);
    }
  }, [jobs, checkJobStatus]);

  const handleViewProgress = useCallback(async (job: JobItem) => {
    setSelectedJob(job);
    setProgressModalOpen(true);
    setJobProgress(null);
    
    try {
      const progress = await checkJobStatus(job.job_id);
      setJobProgress(progress);
    } catch (error) {
      console.error('Error fetching job progress:', error);
      setJobProgress({
        status: 'error',
        error: 'Error al obtener el progreso del trabajo'
      });
    }
  }, [checkJobStatus]);

  const handleViewError = useCallback(async (job: JobItem) => {
    setSelectedJob(job);
    setErrorModalOpen(true);
    setJobProgress(null);
    
    try {
      const response = await fetch(`/check-job-status/${job.job_id}?include_traceback=true`);
      const progress = await response.json();
      setJobProgress(progress);
    } catch (error) {
      console.error('Error fetching job error:', error);
      setJobProgress({
        status: 'error',
        error: 'Error al obtener los detalles del error'
      });
    }
  }, []);

  const getResultUrl = (job: JobItem): string => {
    if (job.status === 'completed') {
      if (job.job_type === 'generate_cp' || job.medio_pago === 'Generación CP') {
        return `/generar-cps-result?job_id=${job.job_id}`;
      } else {
        return `/autorizar-cp-generar-ce-result?job_id=${job.job_id}`;
      }
    } else if (job.status === 'approved') {
      if (job.job_type === 'programacion_pagos') {
        return `/programacion-pagos-confirmar?job_id=${job.job_id}`;
      } else {
        return `/autorizar-cp-generar-ce-result?job_id=${job.job_id}`;
      }
    } else if (job.status === 'draft') {
      return `/programacion-pagos-result?job_id=${job.job_id}`;
    }
    return '#';
  };

  const getDownloadUrl = (job: JobItem): string => {
    if (job.status === 'approved' && job.job_type === 'programacion_pagos') {
      return `/download-programacion-excel/${job.job_id}`;
    }
    return `/download-excel/xlsx?job_id=${job.job_id}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log('Copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'file_name',
      headerName: 'Archivo',
      width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'medio_pago',
      headerName: 'Medio de Pago',
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={getMedioPageText(params.value)}
          size="small"
          icon={params.value === 'Generación CP' ? <Receipt /> : <AccountBalanceWallet />}
          color={params.value === 'Generación CP' ? 'success' : 'primary'}
        />
      )
    },
    {
      field: 'job_type',
      headerName: 'Tipo de Trabajo',
      width: 200,
      renderCell: (params) => (
        <Chip 
          label={getJobTypeText(params.value)}
          size="small"
          icon={getJobTypeIcon(params.value)}
          color="secondary"
        />
      )
    },
    ...(isSuperUser ? [{
      field: 'username',
      headerName: 'Usuario',
      width: 120,
      renderCell: (params: any) => (
        <Chip 
          label={params.value || 'N/A'}
          size="small"
          color="default"
        />
      )
    }] : []),
    {
      field: 'created_at',
      headerName: 'Fecha de Creación',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value as string).toLocaleString('es-CO')}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 130,
      renderCell: (params) => (
        <Chip 
          label={getStatusText(params.value)}
          size="small"
          color={getStatusColor(params.value)}
          icon={getStatusIcon(params.value)}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 280,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const job = params.row as JobItem;
        const buttons = [];

        if (job.status === 'completed' || job.status === 'approved' || job.status === 'draft') {
          buttons.push(
            <Button
              key="view-results"
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Visibility />}
              onClick={() => window.open(getResultUrl(job), '_blank')}
              sx={{ 
                minWidth: 100,
                mr: 0.5,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              Resultados
            </Button>
          );
        }

        if (job.status === 'completed' || job.status === 'approved') {
          buttons.push(
            <Button
              key="download-excel"
              variant="contained"
              color="success"
              size="small"
              startIcon={<Download />}
              onClick={() => window.open(getDownloadUrl(job), '_blank')}
              sx={{ 
                minWidth: 100,
                mr: 0.5,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              Descargar
            </Button>
          );
        }

        if (job.status === 'processing' || job.status === 'queued') {
          buttons.push(
            <Button
              key="view-progress"
              variant="contained"
              color="info"
              size="small"
              startIcon={<PlayArrow />}
              onClick={() => handleViewProgress(job)}
              sx={{ 
                minWidth: 100,
                mr: 0.5,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              Progreso
            </Button>
          );
        }

        if (job.status === 'failed') {
          buttons.push(
            <Button
              key="view-error"
              variant="contained"
              color="error"
              size="small"
              startIcon={<ErrorIcon />}
              onClick={() => handleViewError(job)}
              sx={{ 
                minWidth: 100,
                mr: 0.5,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
            >
              Ver Error
            </Button>
          );
        }

        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            {buttons}
          </Stack>
        );
      }
    }
  ];

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(refreshJobs, 5000);
    return () => clearInterval(interval);
  }, [refreshJobs]);

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <BusinessCenter color="primary" />
            <Typography variant="h5">Estado de Trabajos</Typography>
          </Stack>
        }
        action={
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={refreshJobs}
            disabled={refreshing}
            sx={{ 
              transform: refreshing ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          >
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </Button>
        }
        sx={{ backgroundColor: 'primary.main', color: 'white' }}
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Aquí puede ver el estado de todos sus trabajos de procesamiento.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TreasuryDataGrid
            rows={jobs}
            columns={columns}
            showToolbar={true}
            showExport={true}
            showRefresh={true}
            title="Estado de Trabajos"
            tableId="jobs-dashboard"
            onRefresh={refreshJobs}
            exportOptions={{
              filename: 'estado_trabajos',
              includeHeader: true,
              includeFooter: true
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
              },
            }}
          />
        )}
      </CardContent>

      {/* Progress Modal */}
      <Dialog
        open={progressModalOpen}
        onClose={() => setProgressModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HourglassEmpty color="primary" />
            <Typography variant="h6">Progreso del Trabajo</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!jobProgress ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Cargando información de progreso...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Alert severity={jobProgress.status === 'failed' ? 'error' : jobProgress.status === 'completed' ? 'success' : 'info'}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {getStatusIcon(jobProgress.status)}
                  <Typography variant="body2">
                    <strong>Estado:</strong> {getStatusText(jobProgress.status)}
                  </Typography>
                </Stack>
              </Alert>

              {jobProgress.details?.position_in_queue !== undefined && (
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Posición en cola:</strong> {jobProgress.details.position_in_queue}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Su trabajo se procesará automáticamente cuando los trabajos anteriores finalicen.
                  </Typography>
                </Alert>
              )}

              {jobProgress.details?.operation && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Operación actual:
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {jobProgress.details.operation}
                    </Typography>
                  </Box>
                </Box>
              )}

              {jobProgress.details?.current_item !== undefined && jobProgress.details?.total_items && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Progreso: {jobProgress.details.current_item} de {jobProgress.details.total_items} facturas
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(jobProgress.details.current_item / jobProgress.details.total_items) * 100}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
              )}

              {jobProgress.details?.elapsed_time && (
                <Typography variant="body2">
                  <strong>Tiempo transcurrido:</strong> {jobProgress.details.elapsed_time}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressModalOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ErrorIcon color="error" />
            <Typography variant="h6">Detalles del Error</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!jobProgress ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Cargando detalles del error...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {jobProgress.error && (
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Error:</strong> {jobProgress.error}
                  </Typography>
                </Alert>
              )}

              {selectedJob && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Información del Trabajo:
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>ID:</strong> {selectedJob.job_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Estado:</strong> {getStatusText(selectedJob.status)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Archivo:</strong> {selectedJob.file_name}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Posibles Soluciones:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Verifique que el archivo Excel tenga el formato correcto</li>
                  <li>Asegúrese de que las credenciales SINCO sean válidas</li>
                  <li>Confirme la conectividad con los sistemas externos</li>
                  <li>Si el error persiste, contacte al administrador del sistema</li>
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)}>
            Cerrar
          </Button>
          {jobProgress?.error && (
            <Button
              startIcon={<ContentCopy />}
              onClick={() => copyToClipboard(jobProgress.error || '')}
              color="primary"
            >
              Copiar Error
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default JobsDashboardResult;